// Cliente del flujo de decisión inicial sobre la queja radicada (triple paso):
// contratista/tecnólogo eligen la decisión y arman el documento → abogado especializado
// revisa (aprueba/devuelve) → Director de Instrucción aprueba (materializa) o devuelve.
// Ver backend src/modules/decision-flujo.
import { api } from './api';

export type TipoDecisionInicial =
  | 'AUTO_INHIBITORIO'
  | 'INDAGACION_PREVIA'
  | 'INVESTIGACION';

export type EstadoDecisionInicial =
  | 'PENDIENTE_CONTRATISTA'
  | 'PENDIENTE_ESPECIALISTA'
  | 'PENDIENTE_DIRECTOR'
  | 'DEVUELTA_TECNICO'
  | 'DEVUELTA_ETAPA2'
  | 'DEVUELTA_ESPECIALISTA'
  | 'APROBADA';

export type DestinoDevolucion = 'TECNICO' | 'ETAPA2';

// Decisión tal como la devuelve el detalle del expediente (snake_case).
export interface DecisionInicial {
  id: string;
  expediente_id?: string;
  tipo?: TipoDecisionInicial | null;
  estado: EstadoDecisionInicial;
  observaciones?: string | null;
  motivo_devolucion?: string | null;
  comentarios_director?: string | null;
  documento_datos?: DocumentoBorrador | null; // borrador persistido de la decisión
  // Notificación al personal (capturada al proponer) + revisores asignados.
  notificado?: boolean | null;
  notificacion_constancia_key?: string | null;
  revisor_abogado_id?: string | null;
  revisor_supervisor_id?: string | null;
  propuesta_por?: string | null;
  visto_bueno_por?: string | null;
  aprobada_por?: string | null;
  fecha_propuesta?: string | null;
  fecha_revision?: string | null;
}

/** Candidatos a revisar la decisión (para los selects de envío). */
export interface RevisoresDisponibles {
  abogados: Array<{ id: string; nombre: string }>;
  supervisores: Array<{ id: string; nombre: string }>;
}

// Fila de la bandeja (camelCase, aplanada con datos del expediente).
export interface DecisionBandeja {
  id: string;
  expedienteId: string;
  radicado: string;
  radicadoManual?: string | null;
  investigado?: string | null;
  tipo?: TipoDecisionInicial | null;
  estado: EstadoDecisionInicial;
  observaciones?: string | null;
  motivoDevolucion?: string | null;
  comentariosDirector?: string | null;
  fechaPropuesta?: string | null;
  fechaRevision?: string | null;
}

export const TIPOS_DECISION: Array<{
  v: TipoDecisionInicial;
  label: string;
  norma: string;
  descripcion: string;
}> = [
  {
    v: 'AUTO_INHIBITORIO',
    label: 'Auto inhibitorio',
    norma: 'Art. 209',
    descripcion: 'No hay mérito para iniciar; se archiva la actuación.',
  },
  {
    v: 'INDAGACION_PREVIA',
    label: 'Indagación previa',
    norma: 'Art. 208',
    descripcion: 'Actuación previa para identificar al autor o esclarecer los hechos.',
  },
  {
    v: 'INVESTIGACION',
    label: 'Investigación disciplinaria',
    norma: 'Art. 211-213',
    descripcion: 'Apertura formal de investigación disciplinaria.',
  },
];

export function labelTipoDecision(v?: string | null): string {
  const t = TIPOS_DECISION.find((x) => x.v === v);
  return t ? `${t.label} (${t.norma})` : '—';
}

export const ESTADO_DECISION_LABEL: Record<EstadoDecisionInicial, string> = {
  PENDIENTE_CONTRATISTA: 'Pendiente por contratista (elegir decisión)',
  PENDIENTE_ESPECIALISTA: 'Pendiente por abogado especializado (revisión)',
  PENDIENTE_DIRECTOR: 'Pendiente por director de instrucción',
  DEVUELTA_TECNICO: 'Devuelto al técnico (corregir pre-radicación)',
  DEVUELTA_ETAPA2: 'Devuelto al contratista/tecnólogo (corregir)',
  DEVUELTA_ESPECIALISTA: 'Devuelto al abogado especializado (con comentarios)',
  APROBADA: 'En firme (aprobada por el director)',
};

export function labelEstadoDecision(v?: string | null): string {
  return ESTADO_DECISION_LABEL[v as EstadoDecisionInicial] ?? (v ?? '—');
}

/** "Situación" (por quién está pendiente) para mostrar como control del flujo. */
export function labelSituacion(v?: string | null): string {
  return labelEstadoDecision(v);
}

// ---- Roles que participan en el flujo (para el RBAC de la UI) --------------
export const ROLES_ESPECIALISTA = ['ABOGADO_ESPECIALIZADO', 'ADMINISTRADOR'];
// Revisores de la decisión: abogado especializado + supervisor (el asignado al caso).
export const ROLES_REVISOR_DECISION = ['ABOGADO_ESPECIALIZADO', 'SUPERVISOR', 'ADMINISTRADOR'];
export const ROLES_DIRECTOR_DECISION = ['DIRECTOR_INSTRUCCION', 'ADMINISTRADOR'];
export const ROLES_CORRECTOR_TECNICO = ['TECNICO_ADMINISTRATIVO', 'ADMINISTRADOR'];
export const ROLES_CORRECTOR_ETAPA2 = ['CONTRATISTA_INSTRUCCION', 'TECNOLOGO_APOYO', 'ADMINISTRADOR'];
// Quienes eligen la decisión y arman el documento (contratista + tecnólogo de apoyo).
export const ROLES_PROPONE_DECISION = ['CONTRATISTA_INSTRUCCION', 'TECNOLOGO_APOYO', 'ADMINISTRADOR'];
// Todos los que ven la bandeja / panel (incluye supervisión de solo lectura).
export const ROLES_DECISION_UI = [
  'ABOGADO_ESPECIALIZADO',
  'CONTRATISTA_INSTRUCCION',
  'DIRECTOR_INSTRUCCION',
  'TECNICO_ADMINISTRATIVO',
  'TECNOLOGO_APOYO',
  'SUPERVISOR',
  'APOYO_SUPERVISOR',
  'ADMINISTRADOR',
];

// ---- Llamadas -------------------------------------------------------------
export function bandejaDecisiones(): Promise<DecisionBandeja[]> {
  return api<DecisionBandeja[]>('/decision-inicial/bandeja');
}
export function obtenerDecision(expedienteId: string): Promise<DecisionInicial> {
  return api<DecisionInicial>(`/expedientes/${expedienteId}/decision-inicial`);
}
export function proponerDecision(
  expedienteId: string,
  body: {
    tipo: TipoDecisionInicial;
    notificado?: boolean; // solo aplica a Indagación previa
    notificacionKey?: string;
    revisorAbogadoId: string;
    revisorSupervisorId?: string;
    observaciones?: string;
  },
): Promise<DecisionInicial> {
  return api<DecisionInicial>(`/expedientes/${expedienteId}/decision-inicial/proponer`, {
    method: 'POST',
    body,
  });
}
/** Abogados especializados y supervisores para asignar la revisión de la decisión. */
export function listarRevisores(): Promise<RevisoresDisponibles> {
  return api<RevisoresDisponibles>('/decision-inicial/revisores');
}
export function devolverEspecialista(
  expedienteId: string,
  body: { destino: DestinoDevolucion; motivo: string },
): Promise<DecisionInicial> {
  return api<DecisionInicial>(`/expedientes/${expedienteId}/decision-inicial/devolver`, {
    method: 'POST',
    body,
  });
}
/** Guarda el borrador del documento (campos editados; p. ej. Auto Inhibitorio). */
/**
 * Borrador persistido de la decisión (en `documento_datos`, Json):
 *  - `seleccion`: las respuestas del formulario que determinan QUÉ documentos aplican
 *    (p. ej. prórrogas, versión libre, decisión de indagación).
 *  - `docs`: el texto editado de cada documento, indexado por PlantillaId, para que el
 *    revisor/Director vean EXACTAMENTE lo que armó el contratista.
 * El Auto Inhibitorio guarda sus campos planos en la raíz (compat: firma index).
 */
export interface DocumentoBorrador {
  seleccion?: Record<string, unknown>;
  docs?: Record<string, Record<string, string>>;
  [k: string]: unknown;
}

export function guardarDocumentoDecision(
  expedienteId: string,
  datos: DocumentoBorrador | Record<string, string>,
): Promise<DecisionInicial> {
  return api<DecisionInicial>(`/expedientes/${expedienteId}/decision-inicial/documento`, {
    method: 'POST',
    body: { datos },
  });
}
export function reenviarDecision(expedienteId: string): Promise<DecisionInicial> {
  return api<DecisionInicial>(`/expedientes/${expedienteId}/decision-inicial/reenviar`, {
    method: 'POST',
  });
}
/** El abogado especializado da el visto bueno del documento → pasa al Director. */
export function aprobarEspecialista(expedienteId: string): Promise<DecisionInicial> {
  return api<DecisionInicial>(
    `/expedientes/${expedienteId}/decision-inicial/aprobar-especialista`,
    { method: 'POST' },
  );
}
export function aprobarDecision(expedienteId: string): Promise<DecisionInicial> {
  return api<DecisionInicial>(`/expedientes/${expedienteId}/decision-inicial/aprobar`, {
    method: 'POST',
  });
}
/** El Director devuelve a corregir eligiendo destino (técnico / contratista). */
export function devolverDirector(
  expedienteId: string,
  body: { destino: DestinoDevolucion; motivo: string },
): Promise<DecisionInicial> {
  return api<DecisionInicial>(
    `/expedientes/${expedienteId}/decision-inicial/devolver-director`,
    { method: 'POST', body },
  );
}
